import * as React from "react";
import { FormProvider, useForm } from "react-hook-form";
import * as Dialog from "@radix-ui/react-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Check, Edit, User, X } from "lucide-react";
import {
  GetSnapshotEventResponseItem,
  SnapshotEventReminderFrequency,
} from "@/modules/snapshots/businessReports.types";
import { useDeleteSnapshotEventMutation } from "@/modules/snapshots/businessReports.api";
import { ConfirmDeleteSnapshotEventDialog } from "@/modules/snapshots/snapshotEvents/confirmDeleteSnapshotEventDialog";
import { STATUS_LABEL_MAP } from "@/modules/snapshots/snapshotEvents.consts";
import { SnapshotEventReminderForm } from "@/modules/snapshots/snapshotEvents/components/snapshotEventReminderForm";
import { ReminderSimpleForm } from "@/modules/snapshots/snapshotEvents/components/reminderSimpleForm";
import { computeReminderOffset } from "@/modules/snapshots/snapshotEvents/helpers/computeReminderStartDate";
import { Button } from "@/components/ui/button";
import { parseISO } from "date-fns";
import { sanitizeSnapshotEventHtml } from "@/modules/snapshots/snapshotEvents/sanitize";

interface Props {
  snapshotEvent: GetSnapshotEventResponseItem;
  open: boolean;
  onClose: () => void;
  onEdit: (snapshotEvent: GetSnapshotEventResponseItem) => void;
}

export const ViewSnapshotEventDialog: React.FC<Props> = ({ snapshotEvent, onEdit, onClose }) => {
  const [deleteSnapshotEventMutation] = useDeleteSnapshotEventMutation();

  const [snapshotEventToDelete, setSnapshotEventToDelete] = React.useState<GetSnapshotEventResponseItem | null>();

  const advancedReminderForm = !snapshotEvent?.reminder_start_date;

  const readOnlyFormMethods = useForm<{
    sendReminderBefore: number | null;
    sendReminderBeforeFrequency: SnapshotEventReminderFrequency;
    reminderInterval: number;
    reminderFrequency: SnapshotEventReminderFrequency;
  }>({
    values: {
      sendReminderBefore: snapshotEvent.reminder_start_date
        ? computeReminderOffset(
            parseISO(snapshotEvent.reminder_end_date ?? snapshotEvent.event_date),
            parseISO(snapshotEvent.reminder_start_date),
            snapshotEvent.reminder_frequency as SnapshotEventReminderFrequency,
          )
        : null,
      sendReminderBeforeFrequency: snapshotEvent.reminder_frequency as SnapshotEventReminderFrequency,
      reminderInterval: snapshotEvent.reminder_interval,
      reminderFrequency: snapshotEvent.reminder_frequency as SnapshotEventReminderFrequency,
    },
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100 p-4 font-sans">
      <Dialog.Root open={!!snapshotEvent} onOpenChange={onClose}>
        <Dialog.Trigger asChild></Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black opacity-30" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[95vh] w-[90vw] max-w-4xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 pb-4">
              <Dialog.Title className="text-2xl font-semibold text-gray-800">View Event & Email Alert</Dialog.Title>
              <div className="flex items-center space-x-3">
                {snapshotEvent.status !== "done" && (
                  <button
                    className="flex items-center rounded-md bg-gray-200 px-4 py-2 text-gray-700 transition-colors duration-200 hover:bg-gray-300"
                    type="submit"
                    onClick={() => onEdit(snapshotEvent)}
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Edit
                  </button>
                )}
                <Dialog.Close asChild>
                  <button
                    className="text-gray-500 transition-colors duration-200 hover:text-gray-700"
                    aria-label="Close"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </Dialog.Close>
              </div>
            </div>

            {/* Content Area */}
            <div className="mt-6 flex flex-grow flex-col gap-6 md:flex-row">
              <div className="flex-1 space-y-6">
                {/* Event Status */}
                <div>
                  <div className="block text-sm font-medium text-gray-700">Event Date</div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 shadow-sm">
                    <ul className="space-y-1 text-sm leading-relaxed text-gray-600">
                      <li>• Event created: {snapshotEvent.event_date}</li>
                      {/*<li>• Next reminder: {nearestReminderDate}</li>*/}
                      <li>• Total recipients: {snapshotEvent.recipients.length}</li>
                      <li>
                        • Completion requirement:{" "}
                        {snapshotEvent.required_only_one_done ? "Any one person" : "All persons"}
                      </li>
                      <li>• Status: {STATUS_LABEL_MAP[snapshotEvent.status]}</li>
                    </ul>
                  </div>
                </div>

                {/* Event Date */}
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Event Date</label>
                  <div className="w-full rounded-md border border-gray-300 bg-gray-100 p-3 font-medium text-gray-800">
                    {snapshotEvent.event_date}
                  </div>
                </div>

                {/* Event Title */}
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Event Title</label>
                  <div className="w-full rounded-md border border-gray-300 bg-gray-100 p-3 font-medium text-gray-800">
                    {snapshotEvent.title}
                  </div>
                </div>

                {/* Reminders */}
                {!advancedReminderForm ? (
                  <FormProvider {...readOnlyFormMethods}>
                    <ReminderSimpleForm disabled />
                  </FormProvider>
                ) : (
                  <SnapshotEventReminderForm
                    onSave={() => {}}
                    disabled
                    initialRule={{
                      endDate: new Date(snapshotEvent?.reminder_end_date ?? snapshotEvent!.event_date),
                      frequency: snapshotEvent.reminder_frequency,
                      interval: snapshotEvent.reminder_interval,
                      occurrences: snapshotEvent.reminder_end_after_occurrences,
                      weekDays: snapshotEvent.reminder_week_days ?? [],
                      endType: snapshotEvent.reminder_end_after_occurrences > 0 ? "AFTER_OCCURRENCES" : "NEVER",
                    }}
                  />
                )}
              </div>

              {/* Right Column - Email Message & Recipients */}
              <div className="flex-1 space-y-6">
                {/* Email Message */}
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Email Message</label>
                  <div className="min-h-[150px] w-full rounded-md border border-gray-300 bg-gray-100 p-3 leading-relaxed text-gray-800">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: sanitizeSnapshotEventHtml(snapshotEvent.email_message),
                      }}
                    ></div>
                  </div>
                </div>

                {/* Recipients */}
                <div className="space-y-4">
                  <h3 className="flex items-center text-lg font-medium text-gray-800">
                    <User className="mr-2 h-5 w-5" />
                    Recipients
                  </h3>
                  <div className="space-y-3">
                    {snapshotEvent.recipients.map((recipient, index) => (
                      <div key={recipient.email} className="flex items-center space-x-2">
                        <Checkbox
                          id="include-document"
                          className="mt-0.5 h-5 w-5 rounded border border-gray-300 bg-white"
                          disabled
                          defaultChecked={!recipient.is_readonly}
                        >
                          <Check className="text-white" size={14} />
                        </Checkbox>

                        <label htmlFor={`recipient-${index}`} className="flex-grow cursor-pointer text-gray-700">
                          {recipient.email}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Completion Requirement */}
                {snapshotEvent.recipients.length > 1 && (
                  <>
                    <div className="mt-4 flex items-center space-x-2">
                      <Checkbox
                        id="include-document"
                        className="mt-0.5 h-5 w-5 rounded border border-gray-300 bg-white"
                        defaultChecked={snapshotEvent.include_document_in_attachment}
                        disabled
                      >
                        <Check className="text-white" size={14} />
                      </Checkbox>

                      <label htmlFor="completionRequirement" className="cursor-pointer text-sm text-gray-700">
                        Only one person needs to complete this action
                      </label>
                    </div>
                    <p className="ml-7 text-xs text-gray-500">
                      Event will be marked complete when any one recipient completes their action
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="mt-6 flex items-center justify-end space-x-3 border-t border-gray-200 pt-4">
              <Dialog.Close asChild>
                <button className="rounded-md bg-gray-200 px-5 py-2 text-gray-700 transition-colors duration-200 hover:bg-gray-300">
                  Close
                </button>
              </Dialog.Close>

              <Button variant="destructive" onClick={() => setSnapshotEventToDelete(snapshotEvent)}>
                Delete Alert
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {snapshotEventToDelete && (
        <ConfirmDeleteSnapshotEventDialog
          isOpen={!!snapshotEventToDelete}
          setIsOpen={() => setSnapshotEventToDelete(null)}
          name={snapshotEventToDelete.title || ""}
          confirmCb={() => {
            deleteSnapshotEventMutation(snapshotEventToDelete.id);
            setSnapshotEventToDelete(null);
            onClose();
          }}
        />
      )}
    </div>
  );
};
